import { INVALID_TOKEN_STATUS_CODE } from "@equidefi/constants/auth";
import axios, { type AxiosInstance } from "axios";
import { useAuth, useRestoreUrl } from "hooks/useAuth";
import { useLogoutReason } from "hooks/useLogoutReason";
import useUserToken from "hooks/useUserToken";
import { type FC, type PropsWithChildren, createContext, useMemo } from "react";

interface Props extends PropsWithChildren {
  apiUrl: string;
}

export type ApiClientClass<T> = new (...args: unknown[]) => T;

export const ApiClientContext = createContext<AxiosInstance>(axios);

export const ApiClientProvider: FC<Props> = ({ children, apiUrl }) => {
  const { token } = useAuth();
  const { store } = useRestoreUrl();
  const { setLogoutReason } = useLogoutReason();
  const { clearAccessToken } = useUserToken();

  const client = useMemo(() => {
    const axiosClient = axios.create({ baseURL: apiUrl });

    axios.defaults.baseURL = apiUrl;

    axiosClient.interceptors.request.use(
      (config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error),
    );

    axiosClient.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.status === INVALID_TOKEN_STATUS_CODE) {
          clearAccessToken();
          store(window.location);
          setLogoutReason("expired");
          window.location.href = "/";
        }
        return Promise.reject(error);
      },
    );

    return axiosClient;
  }, [token, apiUrl, clearAccessToken, store]);

  return <ApiClientContext.Provider value={client}>{children}</ApiClientContext.Provider>;
};
